import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Center,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import usePipelineStore from '../../../stores/pipeline-store';
import { StepDrawerSettingsAccordionItem } from './StepDrawerSettingsAccordionItem';
import { StepDrawerAnalyticsAccordionItem } from './StepDrawerAnalyticsAccordionItem';
import { StepDrawerLogsAccordionItem } from './StepDrawerLogsAccordionItem';
import { StepDrawerTransitionsAccordionItem } from './StepDrawerTransitionsAccordionItem';

const DRAWER_SECTIONS = [
    { id: 0, title: 'Settings', Component: StepDrawerSettingsAccordionItem, panelProps: {} },
    { id: 1, title: 'Transitions', Component: StepDrawerTransitionsAccordionItem, panelProps: { pb: 4 } },
    { id: 2, title: 'Logs', Component: StepDrawerLogsAccordionItem, panelProps: { pb: 4 } },
    { id: 3, title: 'Analytics', Component: StepDrawerAnalyticsAccordionItem, panelProps: { pb: 4 } },
] as const;

const COLLAPSE_KEYS = ['collapseSettings', 'collapseTransitions', 'collapseLogs', 'collapseAnalytics'] as const;

type DrawerAccordionProps = {
    stepId: string;
};

export function DrawerAccordion({ stepId }: DrawerAccordionProps) {
    const { pipeline, updateStep } = usePipelineStore((state) => state);
    const step = pipeline.steps[stepId] as PipelineStep;

    const onAccordionChange = useCallback(
        (array: number[]) => {
            COLLAPSE_KEYS.forEach((key, index) => {
                const collapsed = array.indexOf(index) < 0;
                updateStep(stepId, key, collapsed, 'editorSettings');
            });
        },
        [stepId, updateStep]
    );

    if (!step) return null;

    const editorSettings = step.editorSettings as PipelineStep['editorSettings'] & { collapseLogs?: boolean };
    const defaultIndex = DRAWER_SECTIONS.map((section, index) => {
        const collapseKey = COLLAPSE_KEYS[index];
        const collapsed = index === 2 ? (editorSettings.collapseLogs ?? true) : (editorSettings[collapseKey] as boolean);
        return collapsed ? -1 : index;
    }).filter((i) => i >= 0);

    return (
        <Accordion allowMultiple defaultIndex={defaultIndex} onChange={onAccordionChange}>
            {DRAWER_SECTIONS.map(({ id, title, Component, panelProps }) => (
                <AccordionItem key={id}>
                    <h2>
                        <AccordionButton _focus={{ borderWidth: 0 }} _active={{ borderWidth: 0 }}>
                            <Center flex="1">{title}</Center>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel {...panelProps}>
                        <Component stepId={stepId} />
                    </AccordionPanel>
                </AccordionItem>
            ))}
        </Accordion>
    );
}
